import { useEffect, useState } from 'react';
import { pb } from '@/lib/pocketbase';
import { useSearchParams } from 'react-router-dom';

interface Translation {
  translations: {
    np: {
      mainText: string;
      subText: string;
      buttonText: string;
    }
  }
}

interface JoinCommunitySection {
  mainText: string;
  subText: string;
  buttonText: string;
  buttonUrl: URL;
  backgroundImage: string;
  product: string[];
  translations?: Translation;
}

interface JoinCommunityProps {
  productId: string;
}

export const JoinCommunity: React.FC<JoinCommunityProps> = ({ productId }) => {
  const [searchParams] = useSearchParams();
  const [section, setSection] = useState<JoinCommunitySection | null>(null);
  const [loading, setLoading] = useState(true);
  const locale = searchParams.get('locale') || 'en';

  useEffect(() => {
    const loadSection = async () => {
      try {
        const record = await pb.collection('join_community_sections').getFirstListItem(
          `product.id ?~ "${productId}" && isVisible = true`
        );

        if (record) {
          // Get translations
          let translation;
          try {
            translation = await pb.collection('translations').getFirstListItem(
              `collection_name = "join_community_sections" && record_id = "${record.id}"`
            );
          } catch (error) {
            // No translations found
          }

          setSection({
            mainText: record.mainText,
            subText: record.subText,
            buttonText: record.buttonText,
            buttonUrl: new URL(record.buttonUrl),
            backgroundImage: pb.files.getURL(record, record.backgroundImage),
            product: record.product,
            translations: translation
          });
        }
      } catch (error) {
        // Silently handle error - component won't render if no data
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      loadSection();
    }
  }, [productId]);

  const getTranslatedContent = (field: keyof Translation['translations']['np']) => {
    if (locale === 'en') {
      return section?.[field] || '';
    }

    return section?.translations?.translations?.np?.[field] || section?.[field] || '';
  };

  const getFontClass = () => {
    return locale === 'np' ? 'font-noto' : 'font-slate';
  };

  if (loading || !section) return null;

  return (
    <section className="relative w-full h-[200px] overflow-hidden mt-[24px]">
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${section.backgroundImage})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/20" />
      </div>

      <div className="relative h-full w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-full flex items-center justify-between">
          <div className="text-white max-w-xl">
            <h2 className={`text-2xl sm:text-3xl font-bold mb-2 ${getFontClass()}`}>
              {getTranslatedContent('mainText')}
            </h2>
            <p className={`text-sm sm:text-base text-white/90 ${getFontClass()}`}>
              {getTranslatedContent('subText')}
            </p>
          </div>

          <a
            href={section.buttonUrl.toString()}
            target="_blank"
            rel="noopener noreferrer"
            className={`bg-[rgba(214,255,235,1)] text-[rgba(0,63,40,1)] px-6 py-3 rounded-full font-medium 
                     hover:bg-white transition-colors duration-300 whitespace-nowrap ${getFontClass()}`}
          >
            {getTranslatedContent('buttonText')}
          </a>
        </div>
      </div>
    </section>
  );
};




